import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { FullPageSpinner } from '@/components/feedback/FullPageSpinner';
import type { Permission } from './permissions';
import { useAuthStore } from './store';

/** Gate a route on an authenticated session. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children ? <>{children}</> : <Outlet />;
}

/** Gate a route on holding a specific permission (UX only; backend enforces). */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children?: ReactNode;
}) {
  const status = useAuthStore((s) => s.status);
  const has = useAuthStore((s) => s.has);
  const location = useLocation();

  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!has(permission)) return <Navigate to="/403" replace />;
  return children ? <>{children}</> : <Outlet />;
}
